/**
 * Marketing content — concise, trauma-informed, SEO-friendly.
 * Each topic gets a slug, kicker, title, lede, sections, and "what helps" list.
 *
 * Kept intentionally educational (not alarming). Avoid clinical jargon
 * where everyday language works. Where the app's existing Library covers
 * the same topic in depth, the marketing page is a respectful summary
 * with a soft CTA to "Open the app" for deeper guidance.
 */

export const RESOURCES = [
  {
    slug: "gaslighting",
    title: "Gaslighting",
    lede: "A pattern of denying your reality so consistently that you start to distrust your own memory, instincts, and emotions.",
    kicker: "when reality keeps shifting",
    sections: [
      { h: "What it looks like", list: [
        "\"That never happened.\" / \"You're remembering it wrong.\"",
        "\"You're being too sensitive.\" / \"You're overreacting.\"",
        "Saying something on Monday and denying it on Wednesday — repeatedly.",
        "Telling other people you're \"unstable\" so your reports aren't believed.",
      ]},
      { h: "Why it's so disorienting",
        body: "Gaslighting works because we are wired to trust people we love. When the person closest to you keeps rewriting what happened, your brain starts running the math: \"Maybe I'm the problem.\" That self-doubt is the goal, not a side effect." },
      { h: "What helps", list: [
        "Write things down — in your own handwriting or a private note app.",
        "Compare your written record with what you're being told later.",
        "Talk to someone outside the relationship — a friend, therapist, or hotline.",
        "Notice physical signals: confusion, brain fog, second-guessing yourself constantly.",
      ]},
    ],
  },
  {
    slug: "darvo",
    title: "DARVO",
    lede: "Deny, Attack, Reverse Victim and Offender — a four-step move that turns the person who was hurt into the one being accused.",
    kicker: "when raising a concern becomes the crime",
    sections: [
      { h: "The four steps", list: [
        "Deny — \"I didn't do that.\" / \"You're imagining it.\"",
        "Attack — \"You always do this. You're impossible.\"",
        "Reverse roles — \"You're the one being abusive right now.\"",
        "End: You're apologising for raising the concern in the first place.",
      ]},
      { h: "Why it's hard to spot in the moment",
        body: "DARVO escalates fast. Within two minutes you've gone from \"can we talk about something that hurt me\" to defending yourself. Healthy people can be defensive sometimes — DARVO is a pattern, not a one-off." },
      { h: "What helps", list: [
        "Pause the conversation. \"I need a minute\" is a complete sentence.",
        "Write down what you actually wanted to say before re-entering.",
        "Notice the pattern across weeks, not just one fight.",
      ]},
    ],
  },
  {
    slug: "coercive-control",
    title: "Coercive control",
    lede: "A pattern of behaviour that, in aggregate, makes a person feel monitored, dependent, isolated, or afraid — even without physical violence.",
    kicker: "the slow narrowing of a life",
    sections: [
      { h: "What it can look like", list: [
        "Monitoring location, texts, spending, or social media.",
        "Decisions about clothes, food, friends, work made by the partner.",
        "Slow isolation from family and old friends.",
        "Financial control — allowances, blocked accounts, hidden assets.",
        "Threats that escalate when the relationship is questioned.",
      ]},
      { h: "Why \"there's no bruise\" is not the test",
        body: "Coercive control is now recognised in many jurisdictions as a form of domestic abuse on its own. Many survivors describe it as more disorienting than physical violence because it's harder to name." },
    ],
  },
  {
    slug: "trauma-bonding",
    title: "Trauma bonding",
    lede: "A strong emotional attachment to a person who is harming you, formed through cycles of cruelty and reconciliation.",
    kicker: "why leaving feels impossible",
    sections: [
      { h: "Why it forms",
        body: "Trauma bonds are not weakness. They are a nervous system response to intermittent reinforcement — the same mechanism that makes slot machines compelling. The body learns to crave the relief that follows the harm, and the relief is interpreted as love." },
      { h: "What helps", list: [
        "Time and distance. The bond loosens with consistent absence of the cycle.",
        "Naming it out loud, ideally to a therapist who specialises in domestic abuse.",
        "Building a quiet support network before you need it.",
        "Reading the cycle of abuse and recognising where you are in it.",
      ]},
    ],
  },
  {
    slug: "love-bombing",
    title: "Love bombing",
    lede: "An intense, fast-moving idealisation phase at the start of a relationship — gifts, attention, declarations — designed to create dependence quickly.",
    kicker: "when fast feels like fate",
    sections: [
      { h: "What it looks like", list: [
        "Daily \"I've never felt this way\" messages within the first weeks.",
        "Talk of moving in, getting engaged, having kids unusually early.",
        "Calling you their \"soulmate\" before they know your siblings' names.",
        "Extreme generosity, followed by guilt when you don't reciprocate at the same pitch.",
      ]},
      { h: "Why it's so confusing later",
        body: "When the relationship turns difficult, you compare the present to the love-bombing phase and conclude \"they used to love me — I just need to get them back to that.\" The phase wasn't sustainable love. It was an early signal." },
    ],
  },
  {
    slug: "cycle-of-abuse",
    title: "The cycle of abuse",
    lede: "Four phases that often repeat: tension building → incident → reconciliation → calm. Recognising the cycle is often the first step toward clarity.",
    kicker: "the pattern that explains the chaos",
    sections: [
      { h: "The four phases", list: [
        "Tension building — walking on eggshells, sensing something is coming.",
        "Incident — verbal, emotional, financial, or physical harm.",
        "Reconciliation — apologies, promises, gifts, the \"old self\" returns.",
        "Calm — the honeymoon. You think it's finally over. Then tension begins again.",
      ]},
      { h: "Why the calm phase keeps people in",
        body: "Survivors often describe the calm phase as the cruellest part — it's what makes you keep hoping. The cycle is not random; it is the relationship's structure. Identifying which phase you're in right now is itself an act of clarity." },
    ],
  },
  {
    slug: "power-control-wheel",
    title: "The Power & Control Wheel",
    lede: "A framework developed in Duluth, Minnesota in 1984 to map the tactics used to maintain control in abusive relationships.",
    kicker: "naming the tactics",
    sections: [
      { h: "The eight spokes", list: [
        "Using intimidation",
        "Using emotional abuse",
        "Using isolation",
        "Minimising, denying, blaming",
        "Using children",
        "Using male privilege (or institutional power)",
        "Using economic abuse",
        "Using coercion and threats",
      ]},
      { h: "Why it's still relevant 40 years later",
        body: "The Wheel is used by domestic violence advocates worldwide because it makes the invisible visible. Many survivors describe the moment they saw the Wheel as the moment they finally had language for what they'd been living." },
    ],
  },
];

export function getResource(slug) {
  return RESOURCES.find((r) => r.slug === slug) || null;
}
